import { Search, Grid3x3, List, Upload, LogOut, User, FolderSearch, X as XIcon, Menu } from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { TID } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { useMobileSidebar } from "@/contexts/MobileSidebarContext";

export default function TopBar({ view, setView, onSearch, searchValue, onUpload, canUpload, scope }) {
  const { user, logout } = useAuth();
  const { toggle: toggleSidebar } = useMobileSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Resolve the current folder id from the URL — never from async state — so
  // a search never gets "kicked to root" while folders are still loading.
  // Priority: /folders/:id path > ?folder= query > caller-provided scope prop.
  const pathFolderMatch = location.pathname.match(/^\/folders\/([^/]+)/);
  const currentFolderId =
    pathFolderMatch?.[1] || searchParams.get("folder") || scope?.folderId || null;
  const currentFolderName = scope?.folderName || null;

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const term = searchValue?.trim();
    if (!term) return;
    // Preserve folder context on every submit. Recursive scan is opt-in on the
    // backend via `recursive=true`; Search.jsx passes that flag for us.
    if (currentFolderId) {
      navigate(`/search?q=${encodeURIComponent(term)}&folder=${currentFolderId}`);
    } else {
      navigate(`/search?q=${encodeURIComponent(term)}`);
    }
  };

  const scoped = !!currentFolderId;
  const hasQuery = !!(searchValue && searchValue.trim());
  // Iter 77 — Shorter placeholder on narrow viewports so the input is
  // usable at 390px wide. Full placeholder returns at sm (640px+).
  const isNarrow = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 639px)").matches;
  const placeholder = isNarrow
    ? (scoped ? "Search this folder…" : "Search videos…")
    : (currentFolderName
      ? `Search in "${currentFolderName}" & subfolders…`
      : scoped
        ? "Search this folder & subfolders…"
        : "Search all videos by title, description, or tag…");

  const clearSearch = () => {
    onSearch("");
    // If we're on the /search results page, exit back to the folder they came
    // from (or the root). Anywhere else, just clear the input state.
    if (location.pathname.startsWith("/search")) {
      navigate(currentFolderId ? `/folders/${currentFolderId}` : "/");
    }
  };

  return (
    <header
      data-testid={TID.topbar}
      className="sticky top-0 z-30 h-[72px] border-b border-zinc-800/60 bg-[#09090b]/80 backdrop-blur-xl flex items-center px-3 sm:px-4 md:px-8 gap-2 sm:gap-4"
    >
      {/* Iter 77 — Hamburger button. Only visible on <lg to open the
          mobile drawer; completely hidden on desktop (lg:hidden). */}
      <button
        type="button"
        data-testid="mobile-sidebar-toggle"
        onClick={toggleSidebar}
        className="lg:hidden shrink-0 w-9 h-9 rounded-full bg-zinc-900/70 border border-zinc-800/60 flex items-center justify-center text-zinc-200 hover:text-[var(--brand-accent)] hover:border-[var(--brand-accent)]/60 transition"
        aria-label="Open navigation"
      >
        <Menu className="w-4 h-4" />
      </button>

      <form onSubmit={onSearchSubmit} className="flex-1 min-w-0 max-w-2xl relative">
        {scoped ? (
          <FolderSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--brand-accent)]" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        )}
        <input
          data-testid={TID.searchInput}
          value={searchValue}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={placeholder}
          className={`w-full bg-zinc-950/60 rounded-full pl-10 ${scoped && hasQuery ? "pr-10 sm:pr-40" : scoped ? "pr-4 sm:pr-32" : hasQuery ? "pr-10" : "pr-4"} py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-accent)]/30 transition placeholder:text-zinc-600 ${
            scoped ? "border border-[var(--brand-accent)]/40 focus:border-[var(--brand-accent)]/70" : "border border-zinc-800/60 focus:border-[var(--brand-accent)]/60"
          }`}
        />
        {hasQuery && (
          <button
            type="button"
            data-testid="clear-search-btn"
            onClick={clearSearch}
            title={location.pathname.startsWith("/search")
              ? (currentFolderId ? "Clear search and return to this folder" : "Clear search and return to the dashboard")
              : "Clear the search input"}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-zinc-400 hover:text-red-400 hover:bg-zinc-900/60 transition"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        )}
        {scoped && (
          <span
            data-testid="search-scope-badge"
            title={currentFolderName ? `Search is scoped to "${currentFolderName}" and every folder inside it` : "Search is scoped to the current folder and every folder inside it"}
            className={`absolute top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-[0.2em] text-[var(--brand-accent)] bg-[var(--brand-accent)]/10 border border-[var(--brand-accent)]/30 px-2 py-0.5 rounded-full whitespace-nowrap hidden sm:inline-block ${hasQuery ? "right-10" : "right-2"}`}
          >
            In folder + subs
          </span>
        )}
      </form>

      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        <div className="hidden sm:flex items-center bg-zinc-950/60 border border-zinc-800/60 rounded-full p-0.5">
          <button
            data-testid={TID.viewGrid}
            onClick={() => setView("grid")}
            className={`p-1.5 rounded-full transition ${view === "grid" ? "bg-zinc-800 text-[var(--brand-accent)]" : "text-zinc-500 hover:text-zinc-300"}`}
            title="Grid view"
          >
            <Grid3x3 className="w-4 h-4" />
          </button>
          <button
            data-testid={TID.viewList}
            onClick={() => setView("list")}
            className={`p-1.5 rounded-full transition ${view === "list" ? "bg-zinc-800 text-[var(--brand-accent)]" : "text-zinc-500 hover:text-zinc-300"}`}
            title="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>

        {canUpload && (
          <button
            data-testid={TID.uploadBtn}
            onClick={onUpload}
            className="flex items-center gap-2 bg-[var(--brand-accent)] text-black font-semibold rounded-full px-3 sm:px-4 py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition"
          >
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Upload</span>
          </button>
        )}

        {/* Iter 63 — App title anchor. Reads "Digital Asset Manager"
            regardless of brand; the brand identity lives in the sidebar
            logo. Sits just before the avatar so it reads as the app title,
            not a nav item. Hidden on <md so mobile keeps room for search. */}
        <span
          data-testid="topbar-app-title"
          className="hidden md:inline-block whitespace-nowrap text-[11px] uppercase tracking-[0.25em] text-zinc-400 select-none pointer-events-none pr-1"
        >
          Digital Asset Manager
        </span>

        <div className="relative" ref={menuRef}>
          <button
            data-testid={TID.userMenu}
            onClick={() => setMenuOpen((p) => !p)}
            className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800/60 flex items-center justify-center text-zinc-300 hover:border-[var(--brand-accent)]/60 transition"
            title={user?.email}
          >
            <span className="text-xs font-medium">{user?.name?.[0]?.toUpperCase() || "?"}</span>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-11 w-56 bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl p-2 z-50">
              <div className="px-3 py-2 border-b border-zinc-800 mb-1">
                <div className="text-sm font-medium text-zinc-200 truncate">{user?.name}</div>
                <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--brand-accent)] mt-1">{user?.role}</div>
              </div>
              <button
                data-testid={TID.logoutBtn}
                onClick={() => { logout(); navigate("/login"); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-900 rounded transition"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
